import type { ColDef, ValueFormatterParams, ValueGetterParams } from 'ag-grid-enterprise'
import type { DeviceType } from '~/types/device-type'
import type { IIap } from '~/types/iap'

import IapSubTypeCellRenderer from '~/components/CellRenderers/IAPSubTypeCellRenderer'
import { FormatTextCellRenderer } from '~/components/CellRenderers/TextCellRenderer'
import WithSubTextCellRenderer from '~/components/CellRenderers/WithSubTextCellRenderer'
import sharedHeaderTemplate from '~/components/HeaderTemplate'
import { formatDuration } from '~/utils/date'
import { flattenFeatures } from '~/utils/iap'
import { getNumberWithSpaces } from '~/utils/number'
import { iapStatusColDef, withSubTextColDef } from '~/utils/table'

const getColumns = (deviceType: DeviceType): ColDef[] => {
  const columns: ColDef[] = [
    withSubTextColDef(WithSubTextCellRenderer, {
      field: 'sku',
      headerComponentParams: {
        menuIcon: 'fa-bars',
        template: sharedHeaderTemplate({
          headerSubtitle: 'Name',
          headerSubtitleClassname: 'capitalized',
          headerTitle: 'In-App Purchase ID',
        }),
      },
      headerName: '',
      valueGetter: (params: ValueGetterParams<IIap>): any => ({
        subText: params.data?.sku,
        text: params.data?.title,
      }),
    }),
    {
      cellRenderer: IapSubTypeCellRenderer,
      field: 'sub_type',
      filter: true,
      headerName: 'Type',
      suppressSizeToFit: true,
      width: 120,
    },
    iapStatusColDef({
      field: 'status',
    }),
    {
      cellRenderer: FormatTextCellRenderer,
      field: 'price',
      headerName: 'Price',
      maxWidth: 130,
      minWidth: 90,
      valueFormatter: (params: ValueFormatterParams<IIap>): string => {
        return params.value ? `$ ${getNumberWithSpaces(params.value)}` : '--'
      },
    },
    {
      cellRenderer: FormatTextCellRenderer,
      field: 'trial_period_days',
      headerName: 'Free trial period',
      headerTooltip: 'Free trial period',
      minWidth: 100,
      valueFormatter: (params: ValueFormatterParams) => (params.value ? formatDuration(params.value, 'd') : '--'),
      width: 140,
    },
    {
      cellRenderer: FormatTextCellRenderer,
      field: 'subscription_period_days',
      headerName: 'Subscription period',
      headerTooltip: 'Subscription period',
      minWidth: 100,
      valueFormatter: (params: ValueFormatterParams) => (params.value ? formatDuration(params.value, 'd') : '--'),
      width: 140,
    },
    {
      field: 'unlockables',
      headerName: 'Unlockables',
      tooltipComponent: 'customTooltip',
      valueFormatter: ({ value }: ValueFormatterParams<IIap, IIap['unlockables']>): string =>
        value ? `${flattenFeatures(value).length.toString()} features` : '',
    },
  ]

  if (deviceType === 'ios') {
    columns.push(
      withSubTextColDef(WithSubTextCellRenderer, {
        field: 'subscription_group_name',
        headerComponentParams: {
          menuIcon: 'fa-bars',
          template: sharedHeaderTemplate({
            headerSubtitle: 'ID',
            headerSubtitleClassname: 'capitalized',
            headerTitle: 'Group',
          }),
        },
        headerName: '',
        valueGetter: (params: ValueGetterParams<IIap>): any => ({
          subText: params.data?.subscription_group_id,
          text: params.data?.subscription_group_name,
        }),
      })
    )
  }

  return columns
}

export default getColumns
